import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, message, Modal, Table } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { ExportToCsv } from 'export-to-csv';
import { useForm } from 'antd/lib/form/Form';
import { ROLE_TYPES, CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import {
	brandAssetGroupsRestCrudThunksForBuyer,
	brandAssetGroupsRestCrudThunksForSupplier,
} from '../../thunks/brand_asset_groups_thunks';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import UploadWithSampleModalBase from '../flatfile/UploadWithSampleModal';
import FlatfileImporter from '../flatfile/FlatfileImporter';

const UploadWithSampleModal = UploadWithSampleModalBase as any;

const ASSET_GROUP_FLATFILE_TYPE = 'asset_group';

const getAssetGroupFlatfileFields = (assetTypes: { label: string; value: any }[] = []) => [
	{
		label: 'Asset Name',
		key: 'name',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Asset Type',
		key: 'typeId',
		type: 'enum',
		config: {
			options: assetTypes,
		},
		constraints: [{ type: 'required' }],
	},
];

interface AddOrEditAssetGroupModalProps {
	brandId: number | string;
	assetGroup?: any;
	onCancel: () => void;
	onSuccess: () => void;
	create: (entity: any, primaryKey: string) => Promise<any>;
	update: (entity: any) => Promise<any>;
	fetchAssetTypes: () => Promise<any[]>;
}

const AddOrEditAssetGroupModal: FC<AddOrEditAssetGroupModalProps> = ({
	brandId,
	assetGroup,
	onCancel,
	onSuccess,
	create,
	update,
	fetchAssetTypes,
}): React.ReactElement => {
	const [form] = useForm();
	const [saving, setSaving] = useState(false);
	const [uploadVisible, setUploadVisible] = useState(false);
	const [assetTypeOptions, setAssetTypeOptions] = useState<{ label: string; value: any }[]>([]);
	const [assets, setAssets] = useState<any[]>((assetGroup && assetGroup.assetTemplates) || []);
	const [editVisible, setEditVisible] = useState(false);

	const isUpdate = useMemo(() => !!(assetGroup && assetGroup.id), [assetGroup]);

	useEffect(() => {
		fetchAssetTypes()
			.then((types: any[]) =>
				setAssetTypeOptions(types.map((t) => ({ label: t.name, value: t.id })))
			)
			.catch(() => message.error('Failed to load asset types.'));
	}, [fetchAssetTypes]);

	const flatfileFields = useMemo(
		() => getAssetGroupFlatfileFields(assetTypeOptions),
		[assetTypeOptions]
	);

	const assetTableColumns = useMemo(
		() => [
			{ title: 'Asset Name', dataIndex: 'name', key: 'name' },
			{
				title: 'Type',
				dataIndex: 'typeName',
			},
		],
		[]
	);

	const assetTypeMap = useMemo(
		() => Object.fromEntries(assetTypeOptions.map((t) => [String(t.value), t.label])),
		[assetTypeOptions]
	);

	const enrichWithTypeName = useCallback(
		(data: any[]) =>
			data.map((item) => ({
				...item,
				typeName: item.typeName || assetTypeMap[String(item.typeId)] || item.typeId || '--',
			})),
		[assetTypeMap]
	);

	const showEdit = useCallback(() => setEditVisible(true), []);
	const hideEdit = useCallback(() => setEditVisible(false), []);
	const showUpload = useCallback(() => setUploadVisible(true), []);
	const hideUpload = useCallback(() => setUploadVisible(false), []);
	const submitForm = useCallback(() => form.submit(), [form]);

	const onDataUpload = useCallback(
		(result) =>
			new Promise<string>((resolve) => {
				setAssets(enrichWithTypeName(result.validData));
				resolve('Asset details uploaded successfully!');
				setTimeout(hideUpload, 0);
			}),
		[hideUpload, enrichWithTypeName]
	);

	const onEditDataLoad = useCallback(
		(result) =>
			new Promise<string>((resolve) => {
				setAssets(enrichWithTypeName(result.validData));
				resolve('Asset details updated!');
				setTimeout(hideEdit, 0);
			}),
		[hideEdit, enrichWithTypeName]
	);

	const onDownloadSample = useCallback(() => {
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'asset_group_sample',
			title: 'asset_group_sample',
			headers: getAssetGroupFlatfileFields(assetTypeOptions).map((_) => _.label),
		});
		csvExporter.generateCsv([{ name: 'Example Asset', typeId: '' }]);
	}, [assetTypeOptions]);

	const onSubmit = useCallback(
		(values) => {
			const commonValues = {
				assetTemplates: assets,
				brandId: parseInt(String(brandId)),
			};
			const entity = isUpdate
				? { ...assetGroup, ...values, ...commonValues }
				: { ...values, ...commonValues };
			setSaving(true);
			(isUpdate ? update(entity) : create(entity, 'id'))
				.then(() => {
					message.success(`Asset group ${isUpdate ? 'updated' : 'added'} successfully!`);
					onSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[assets, assetGroup, brandId, create, isUpdate, onSuccess, update]
	);

	return (
		<>
			<Modal
				visible={!uploadVisible && !editVisible}
				title={`${isUpdate ? 'Edit' : 'Add'} Asset Group`}
				closable={false}
				width={600}
				footer={[
					<Button key="cancel" size="large" onClick={onCancel}>
						Cancel
					</Button>,
					<Button key="submit" type="primary" size="large" loading={saving} onClick={submitForm}>
						{isUpdate ? 'Update' : 'Create'}
					</Button>,
				]}
			>
				<Form
					layout="vertical"
					initialValues={{ name: assetGroup && assetGroup.name }}
					onFinish={onSubmit}
					form={form}
				>
					<Form.Item
						name="name"
						label="Group Name"
						rules={[{ required: true, message: 'Group name is required.' }]}
					>
						<Input placeholder="Group name" />
					</Form.Item>
					<div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
						<span className="ant-form-item-label">
							<label>Asset Details</label>
						</span>
						{assets.length > 0 && (
							<div className="flex flex-row gap-2">
								<Button size="small" onClick={showEdit}>
									Edit
								</Button>
								<Button size="small" icon={<UploadOutlined />} onClick={showUpload}>
									Replace
								</Button>
							</div>
						)}
					</div>
					{assets.length > 0 ? (
						<Table
							dataSource={assets}
							columns={assetTableColumns}
							rowKey={(_, index) => index}
							pagination={false}
							size="small"
							className="no-row-hover"
						/>
					) : (
						<Button icon={<UploadOutlined />} onClick={showUpload}>
							Upload Asset Details
						</Button>
					)}
				</Form>
			</Modal>
			{uploadVisible && (
				<UploadWithSampleModal
					type={ASSET_GROUP_FLATFILE_TYPE}
					title="Upload asset details for this group"
					downloadText="Download a sample file or upload asset details for this group."
					sampleFileText="Download sample"
					onCancel={hideUpload}
					onDownloadSample={onDownloadSample}
					fields={flatfileFields}
					onDataLoad={onDataUpload}
					downloading={false}
					showWarningMessage={false}
				/>
			)}
			{editVisible && (
				<FlatfileImporter
					dataLoadComplete={editVisible}
					type={ASSET_GROUP_FLATFILE_TYPE}
					fields={flatfileFields}
					data={assets}
					onComplete={hideEdit}
					onDataLoad={onEditDataLoad}
				/>
			)}
		</>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	create: (entity, primaryKey) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandAssetGroupsRestCrudThunksForSupplier.create(entity, primaryKey)
				: brandAssetGroupsRestCrudThunksForBuyer.create(entity, primaryKey)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandAssetGroupsRestCrudThunksForSupplier.update(entity)
				: brandAssetGroupsRestCrudThunksForBuyer.update(entity)
		),
	fetchAssetTypes: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readLite({ no_pagination: true })
				: assetTypesRestCrudThunksForBuyer.readLite({ no_pagination: true })
		),
});

const ComponentWithoutUserType = connect(null, mapDispatchToProps)(AddOrEditAssetGroupModal as any);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
